import { Suspense, useMemo, useState } from 'react';
import { BoardView as BoardViewSchema, type ModuleRendererProps } from '@momentum/shared';
import { Board2D } from './Board2D.tsx';
import { BoardPanel } from './BoardPanel.tsx';
import { useBoard3D } from './registry3d.ts';
import { isMomentumWorld } from './momentumWorld.ts';

/**
 * The single client renderer registered under the `board` key (both `poc-board` and `fixture-dice-board`
 * declare `capabilities.renderer: 'board'`, so one component serves both — the difference is entirely in the
 * `BoardView` each module's `project()` produces). Parses the module-opaque `instance.view`, then renders the
 * always-available 2D SVG board (`Board2D`, no `three` dependency) plus a "2D / 3D" toggle that only appears
 * once a 3D renderer has registered itself via `registerBoard3D` (see `registry3d.ts`) — normally the client
 * app's `setupRenderers()` at startup. `BoardPanel` (status, progress, round, controls) is shared unchanged
 * between the 2D and 3D visual modes, so only one is ever mounted.
 */
export function BoardRenderer(props: ModuleRendererProps) {
  const { instance, session, you, send, onEvent, reducedMotion, readOnly } = props;
  const view = useMemo(() => BoardViewSchema.parse(instance.view), [instance.view]);
  const Board3D = useBoard3D();
  const [rollPending, setRollPending] = useState(false);
  const [rollError, setRollError] = useState('');
  const [travelBusy, setTravelBusy] = useState(false);
  const [mode, setMode] = useState<'2d' | '3d'>(isMomentumWorld(view.graph) ? '3d' : '2d');
  const activeMode = Board3D ? mode : '2d';
  const slice = isMomentumWorld(view.graph);

  const rollControl = view.controls.find(c => c.action === 'roll');
  const rollEnabled = !!rollControl?.enabled && !readOnly && !travelBusy && !rollPending;
  const roll = async () => {
    if (!rollEnabled || !rollControl) return;
    setRollPending(true); setRollError('');
    try { const result = await send(rollControl.action, rollControl.payload); if (!result.ok) setRollError(result.message || result.code); }
    catch { setRollError('Could not roll. Try again.'); }
    finally { setRollPending(false); }
  };
  /**
   * The in-world chevrons address a route by destination space id; this resolves it back to the
   * very same authorised control the accessible button would have sent, so the 3D affordance can
   * never reach an action the panel would not.
   */
  const route = async (to: string) => {
    const control = view.controls.find(c => c.action === 'route' && (c.payload as { to?: unknown }).to === to);
    if (!control?.enabled || readOnly || travelBusy || rollPending) return;
    setRollError('');
    try { const result = await send(control.action, control.payload); if (!result.ok) setRollError(result.message || result.code); }
    catch { setRollError('Could not choose that route. Try again.'); }
  };
  const modeControls = (
        <div role="group" aria-label="Board view mode" style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            aria-pressed={activeMode === '2d'}
            onClick={() => setMode('2d')}
            style={{ minHeight: 'var(--touch-target, 44px)', padding: '6px 12px' }}
          >
            2D
          </button>
          <button
            type="button"
            aria-pressed={activeMode === '3d'}
            onClick={() => setMode('3d')}
            style={{ minHeight: 'var(--touch-target, 44px)', padding: '6px 12px' }}
          >
            3D
          </button>
        </div>
  );

  return (
    <div className={slice ? 'momentum-board-slice' : undefined} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}>
      {Board3D && (slice ? <details className="momentum-display-tools">
        <summary>Display</summary>
        {modeControls}
      </details> : modeControls)}

      {activeMode === '3d' && Board3D ? (
        <Suspense fallback={<p>Loading 3D board…</p>}>
          <Board3D view={view} you={you} onEvent={onEvent} reducedMotion={reducedMotion} onBusyChange={setTravelBusy} onRoll={roll} rollEnabled={rollEnabled} onRoute={route} />
        </Suspense>
      ) : (
        <Board2D view={view} you={you} onEvent={onEvent} reducedMotion={reducedMotion} />
      )}

      {rollError && <p role="alert">{rollError}</p>}
      <BoardPanel view={travelBusy || rollPending ? { ...view, controls: view.controls.map(c => ({ ...c, enabled: false })) } : view} readOnly={readOnly} send={send} participants={session.participants} youId={you?.participantId ?? null} routeControlsInWorld={activeMode === '3d' && slice} />
    </div>
  );
}
